import React, { useState } from 'react';
import {
    Box, Button, Container, Grid, Typography, Paper, Chip, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination,
    Tabs, Tab,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
// import TableChartIcon from '@mui/icons-material/TableChart';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const summaryCards = [
    { label: 'Critical Alerts', value: 18, change: '−28% vs Last 7 Days', up: false, color: '#d32f2f', bg: '#ffebee', icon: '🛡' },
    { label: 'High Alerts', value: 42, change: '−15% vs Last 7 Days', up: false, color: '#ed6c02', bg: '#fff3e0', icon: '⚠️' },
    { label: 'Medium Alerts', value: 76, change: '−5% vs Last 7 Days', up: false, color: '#f9a825', bg: '#fffde7', icon: '🔔' },
    { label: 'Total Alerts', value: 136, change: '+12% vs Last 7 Days', up: true, color: '#1976d2', bg: '#e3f2fd', icon: '✉️' },
    { label: 'Resolved Alerts', value: 89, change: '+20% vs Last 7 Days', up: true, color: '#2e7d32', bg: '#e8f5e9', icon: '✅' },
];

const categoryData = [
    { name: 'Revenue Protection', value: 32, pct: '23.5%', color: '#1976d2' },
    { name: 'Fraud Detection', value: 24, pct: '17.6%', color: '#ed6c02' },
    { name: 'Digital Adoption', value: 18, pct: '13.2%', color: '#2e7d32' },
    { name: 'Settlement Risk', value: 16, pct: '11.8%', color: '#5c35a0' },
    { name: 'Service Efficiency', value: 20, pct: '14.7%', color: '#00acc1' },
    { name: 'Asset Reliability', value: 14, pct: '10.3%', color: '#f9a825' },
    { name: 'Staff Performance', value: 8, pct: '5.9%', color: '#e91e63' },
    { name: 'Passenger Experience', value: 4, pct: '2.9%', color: '#90a4ae' },
];

const trendData = [
    { date: '02 May', critical: 12, high: 28, medium: 35, low: 8 },
    { date: '07 May', critical: 15, high: 32, medium: 38, low: 9 },
    { date: '12 May', critical: 10, high: 30, medium: 42, low: 10 },
    { date: '17 May', critical: 18, high: 35, medium: 40, low: 11 },
    { date: '22 May', critical: 14, high: 38, medium: 45, low: 10 },
    { date: '27 May', critical: 20, high: 42, medium: 48, low: 12 },
    { date: '31 May', critical: 18, high: 42, medium: 46, low: 13 },
];

const depotAlertData = [
    { depot: 'Colombo Depot', alerts: 28 },
    { depot: 'Kandy Depot', alerts: 21 },
    { depot: 'Kurunegala Depot', alerts: 16 },
    { depot: 'Matara Depot', alerts: 13 },
    { depot: 'Galle Depot', alerts: 11 },
    { depot: 'Jaffna Depot', alerts: 9 },
    { depot: 'Batticaloa Depot', alerts: 8 },
    { depot: 'Anuradhapura Depot', alerts: 7 },
    { depot: 'Puttalam Depot', alerts: 6 },
    { depot: 'Badulla Depot', alerts: 5 },
];

const alertRows = [
    { id: 'AI-2026-0001', title: 'Bus Revenue Significantly Lower Than Expected', category: 'Revenue Protection', entity: 'Bus NB-2567', severity: 'Critical', detectedOn: '30/05/2026 09:45', status: 'New', confidence: '92%' },
    { id: 'AI-2026-0002', title: 'Conductor Repeated Tap Failures Detected', category: 'Fraud Detection', entity: 'Conductor EMP1025', severity: 'High', detectedOn: '30/05/2026 09:20', status: 'New', confidence: '89%' },
    { id: 'AI-2026-0003', title: 'Digital Transaction Share Below Threshold', category: 'Digital Adoption', entity: 'Kandy Depot', severity: 'Medium', detectedOn: '30/05/2026 08:50', status: 'In Progress', confidence: '76%' },
    { id: 'AI-2026-0004', title: 'HO Payable Aging Above Normal Range', category: 'Settlement Risk', entity: 'Head Office', severity: 'High', detectedOn: '30/05/2026 08:30', status: 'New', confidence: '91%' },
    { id: 'AI-2026-0005', title: 'Route Load Factor Drastically Dropped', category: 'Service Efficiency', entity: 'Route 017', severity: 'Medium', detectedOn: '30/05/2026 07:55', status: 'New', confidence: '74%' },
    { id: 'AI-2026-0006', title: 'High Breakdown Probability for Bus', category: 'Asset Reliability', entity: 'Bus NB-3344', severity: 'High', detectedOn: '30/05/2026 07:20', status: 'New', confidence: '87%' },
    { id: 'AI-2026-0007', title: 'High No-Ticket Cases Detected', category: 'Staff Performance', entity: 'Conductor EMP1044', severity: 'Critical', detectedOn: '30/05/2026 06:40', status: 'Escalated', confidence: '93%' },
    { id: 'AI-2026-0008', title: 'Failed Tap Rate Spike on Route', category: 'Passenger Experience', entity: 'Route 025', severity: 'Medium', detectedOn: '30/05/2026 06:10', status: 'New', confidence: '70%' },
];

const busRiskRows = [
    { busNo: 'NB-2567', score: 92, level: 'Critical' },
    { busNo: 'NB-3344', score: 88, level: 'High' },
    { busNo: 'NB-1123', score: 76, level: 'High' },
    { busNo: 'NB-9901', score: 68, level: 'Medium' },
    { busNo: 'NB-7782', score: 64, level: 'Medium' },
];

const predictiveInsights = [
    { title: 'High Breakdown Risk', period: 'Next 7 Days', value: '7 buses', sub: 'are at high risk of breakdown', color: '#d32f2f', icon: '🚌' },
    { title: 'Settlement Delay Risk', period: 'Next 7 Days', value: '3 depots', sub: 'likely to delay settlements', color: '#ed6c02', icon: '📋' },
    { title: 'Revenue Drop Risk', period: 'Next 7 Days', value: '5 routes', sub: 'may underperform next week', color: '#f9a825', icon: '📉' },
    { title: 'Digital Adoption Risk', period: 'Next 30 Days', value: '4 depots', sub: 'at risk of low digital growth', color: '#1976d2', icon: '💻' },
    { title: 'Staff Risk', period: 'Next 7 Days', value: '12 conductors', sub: 'show high variance behavior', color: '#2e7d32', icon: '👥' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SeverityChip = ({ severity }) => {
    const map = { Critical: '#d32f2f', High: '#ed6c02', Medium: '#f9a825', Low: '#2e7d32' };
    return <Chip label={severity} size="small"
        sx={{ backgroundColor: map[severity] || '#999', color: '#fff', fontWeight: 600, fontSize: 11, minWidth: 65 }} />;
};

const StatusChip = ({ status }) => {
    const map = { New: '#1976d2', 'In Progress': '#5c35a0', Escalated: '#d32f2f', Resolved: '#2e7d32' };
    return <Chip label={status} size="small" variant="outlined"
        sx={{ borderColor: map[status] || '#999', color: map[status] || '#999', fontWeight: 600, fontSize: 11, minWidth: 75 }} />;
};

const RiskChip = ({ level }) => {
    const map = { Critical: '#d32f2f', High: '#ed6c02', Medium: '#f9a825' };
    return <Chip label={level} size="small"
        sx={{ backgroundColor: map[level] || '#999', color: '#fff', fontWeight: 600, fontSize: 11, minWidth: 65 }} />;
};

const MiniSparkline = ({ color }) => (
    <Box sx={{ width: 60, height: 24 }}>
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
                <Line type="monotone" dataKey="high" stroke={color} strokeWidth={1.5} dot={false} />
            </LineChart>
        </ResponsiveContainer>
    </Box>
);

const usePagination = (defaultRows = 10) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRows);
    return {
        page, rowsPerPage,
        handleChangePage: (_, p) => setPage(p),
        handleChangeRowsPerPage: (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); },
        startIndex: page * rowsPerPage,
    };
};

// ─── Component ────────────────────────────────────────────────────────────────

const AIAlertAnalysisPage = () => {
    const [riskTab, setRiskTab] = useState(0);
    const pag = usePagination();
    const thSx = { py: 1 };
    const tdSx = { py: 0 };

    return (
        <Container component="main" maxWidth="full" sx={{ py: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>

                {/* ── Page Title ── */}
                <Box sx={{ width: '100%', mb: 2, mt: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        AI Alert and Analysis System
                    </Typography>
                </Box>

                {/* ── Summary Cards ── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: 2,
                        width: '100%',
                        mb: 3
                    }}
                >
                    {summaryCards.map((card) => (
                        <Paper
                            key={card.label}
                            variant="outlined"
                            sx={{ p: 2, borderRadius: 2 }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box
                                    sx={{
                                        backgroundColor: card.bg,
                                        borderRadius: 1,
                                        px: 0.8,
                                        py: 0.3,
                                        fontSize: 18
                                    }}
                                >
                                    {card.icon}
                                </Box>

                                <Box sx={{ ml: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                                        {card.label}
                                    </Typography>

                                    <Typography variant="h5" sx={{ fontWeight: 700, color: card.color }}>
                                        {card.value}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                        {card.up ? (
                                            <TrendingUpIcon sx={{ fontSize: 12, color: '#2e7d32' }} />
                                        ) : (
                                            <TrendingDownIcon sx={{ fontSize: 12, color: '#d32f2f' }} />
                                        )}

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: card.up ? '#2e7d32' : '#d32f2f',
                                                fontWeight: 600
                                            }}
                                        >
                                            {card.change}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ height: 36, mt: 0.5 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={trendData}>
                                        <Line
                                            type="monotone"
                                            dataKey="high"
                                            stroke={card.color}
                                            strokeWidth={1.5}
                                            dot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    ))}
                </Box>

                {/* ── Charts Row: Category Donut | Trend Lines | Depot Bar ── */}
                <Grid container sx={{ mb: 3, width: '100%' }}>

                    {/* Alerts Summary by Category — Donut */}
                    <Grid item xs={12} md={4} sx={{ pr: { xs: 0, md: 2 } }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Alerts Summary by Category</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={categoryData} cx="50%" cy="50%" innerRadius={45} outerRadius={68}
                                                dataKey="value" paddingAngle={2}>
                                                {categoryData.map((entry, i) => (
                                                    <Cell key={i} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(v, n) => [`${v}`, n]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1976d2', lineHeight: 1 }}>136</Typography>
                                        <Typography sx={{ fontSize: 9, color: '#888' }}>Total Alerts</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    {categoryData.map((item) => (
                                        <Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: item.color, flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: 10 }}>{item.name}</Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: 10, fontWeight: 600 }}>{item.value} ({item.pct})</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Alerts Trend (Last 30 Days) */}
                    <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Alerts Trend (Last 30 Days)</Typography>
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={trendData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                                    <YAxis tick={{ fontSize: 9 }} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 10 }} />
                                    <Line type="monotone" dataKey="critical" name="Critical" stroke="#d32f2f" strokeWidth={2} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="high" name="High" stroke="#ed6c02" strokeWidth={2} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="medium" name="Medium" stroke="#f9a825" strokeWidth={2} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="low" name="Low" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Alerts by Depot Top 10 — Horizontal Bar */}
                    <Grid item xs={12} md={4} sx={{ pl: { xs: 0, md: 2 } }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Alerts by Depot (Top 10)</Typography>
                                <Typography variant="caption" color="text.secondary">Alerts</Typography>
                            </Box>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={depotAlertData} layout="vertical"
                                    margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                                    <XAxis type="number" tick={{ fontSize: 9 }} domain={[0, 30]} />
                                    <YAxis type="category" dataKey="depot" tick={{ fontSize: 9 }} width={115} />
                                    <Tooltip />
                                    <Bar dataKey="alerts" fill="#1976d2" radius={[0, 3, 3, 0]}
                                        label={{ position: 'right', fontSize: 9, fill: '#555' }} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── Recent AI Alerts Table + Top Risk Entities ── */}
                <Grid container sx={{ mb: 3, width: '100%' }}>

                    {/* Recent AI Alerts Table */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* <TableChartIcon sx={{ color: '#1976d2', fontSize: 20 }} /> */}
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Recent AI Alerts</Typography>
                            </Box>
                        </Box>

                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                        <TableCell sx={thSx}>Alert ID</TableCell>
                                        <TableCell sx={thSx}>Alert Title</TableCell>
                                        <TableCell sx={thSx}>Category</TableCell>
                                        <TableCell sx={thSx}>Entity</TableCell>
                                        <TableCell sx={thSx}>Severity</TableCell>
                                        <TableCell sx={thSx}>Detected On</TableCell>
                                        <TableCell sx={thSx}>Status</TableCell>
                                        <TableCell sx={thSx}>Confidence</TableCell>
                                        <TableCell sx={thSx}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {alertRows
                                        .slice(pag.startIndex, pag.startIndex + pag.rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell sx={tdSx}>
                                                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600, fontSize: 11 }}>
                                                        {row.id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ ...tdSx, maxWidth: 180 }}>
                                                    <Typography variant="body2" sx={{ fontSize: 11 }}>{row.title}</Typography>
                                                </TableCell>
                                                <TableCell sx={tdSx}>
                                                    <Typography variant="body2" sx={{ fontSize: 11 }}>{row.category}</Typography>
                                                </TableCell>
                                                <TableCell sx={tdSx}>
                                                    <Typography variant="body2" sx={{ fontSize: 11 }}>{row.entity}</Typography>
                                                </TableCell>
                                                <TableCell sx={tdSx}><SeverityChip severity={row.severity} /></TableCell>
                                                <TableCell sx={tdSx}>
                                                    <Typography variant="body2" sx={{ fontSize: 11 }}>{row.detectedOn}</Typography>
                                                </TableCell>
                                                <TableCell sx={tdSx}><StatusChip status={row.status} /></TableCell>
                                                <TableCell sx={tdSx}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 11 }}>{row.confidence}</Typography>
                                                </TableCell>
                                                <TableCell sx={tdSx}>
                                                    <Button size="small" endIcon={<KeyboardArrowDownIcon />}
                                                        sx={{ minWidth: 0, p: '2px 6px', borderRadius: '4px', border: '1px solid #ccc', color: '#555', textTransform: 'none', fontSize: 11 }}>
                                                        View
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Showing 1 to 8 of 136 alerts
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TablePagination
                                        showFirstButton showLastButton
                                        component="div"
                                        count={alertRows.length}
                                        page={pag.page}
                                        onPageChange={pag.handleChangePage}
                                        rowsPerPage={pag.rowsPerPage}
                                        onRowsPerPageChange={pag.handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50, 100]}
                                    />
                                    <Button size="small" sx={{ textTransform: 'none', color: '#1976d2', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        View All Alerts
                                    </Button>
                                </Box>
                            </Box>
                        </TableContainer>
                    </Grid>

                    {/* Top Risk Entities */}
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>Top Risk Entities</Typography>
                        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                            <Tabs value={riskTab} onChange={(_, v) => setRiskTab(v)}
                                sx={{
                                    borderBottom: '1px solid #e0e0e0', minHeight: 36,
                                    '& .MuiTab-root': { minHeight: 36, fontSize: 12, textTransform: 'none', fontWeight: 600 }
                                }}>
                                <Tab label="Buses" />
                                <Tab label="Conductors" />
                                <Tab label="Depots" />
                                <Tab label="Routes" />
                            </Tabs>

                            <Box sx={{ p: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                            <TableCell sx={{ py: 0.5, fontSize: 11 }}>Bus No.</TableCell>
                                            <TableCell sx={{ py: 0.5, fontSize: 11 }}>Risk Score</TableCell>
                                            <TableCell sx={{ py: 0.5, fontSize: 11 }}>Risk Level</TableCell>
                                            <TableCell sx={{ py: 0.5, fontSize: 11 }}>Trend (7 Days)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {busRiskRows.map((row) => (
                                            <TableRow key={row.busNo}>
                                                <TableCell sx={{ py: 0, fontSize: 12, fontWeight: 600 }}>{row.busNo}</TableCell>
                                                <TableCell sx={{ py: 0, fontSize: 12 }}>{row.score}</TableCell>
                                                <TableCell sx={{ py: 0 }}><RiskChip level={row.level} /></TableCell>
                                                <TableCell sx={{ py: 0 }}>
                                                    <MiniSparkline color={row.level === 'Critical' ? '#d32f2f' : row.level === 'High' ? '#ed6c02' : '#f9a825'} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>

                            <Box sx={{ px: 2, pb: 1.5 }}>
                                <Button size="small" sx={{ textTransform: 'none', color: '#1976d2', fontWeight: 600, p: 0 }}>
                                    View Full Risk Analysis
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── Predictive Insights ── */}
                <Box sx={{ width: '100%', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Predictive Insights</Typography>
                    <Grid container spacing={2}>
                        {predictiveInsights.map((insight) => (
                            <Grid item xs={12} sm={6} md key={insight.title} sx={{ flexGrow: 1 }}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ color: insight.color, fontWeight: 700, fontSize: 12, lineHeight: 1 }}>
                                        {insight.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">{insight.period}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: insight.color, lineHeight: 1}}>
                                                {insight.value}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">{insight.sub}</Typography>
                                        </Box>
                                        {/* <Typography sx={{ fontSize: 28, opacity: 0.4 }}>{insight.icon}</Typography> */}
                                    </Box>
                                    <Button size="small" variant="outlined"
                                        sx={{
                                            mt: 1.5, borderRadius: '4px', textTransform: 'none', fontWeight: 600,
                                            fontSize: 11, height: 28, borderColor: insight.color, color: insight.color
                                        }}>
                                        View Details
                                    </Button>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

            </Box>
        </Container>
    );
};

export default AIAlertAnalysisPage;